import {
  accountPaths,
  factorsQuery,
  meQuery,
  preferencesQuery,
  secondStepSearch,
  sessionsQuery,
  signInHref,
  signInSearch,
} from "@nexxt/account-ui";
import { ApiRequestError } from "@nexxt/api-client";
import { RouteError } from "@nexxt/app-kit";
import { listCustomerClaimsInput } from "@nexxt/contracts/claims";
import { listCustomerDocumentsInput } from "@nexxt/contracts/documents";
import { listCustomerFulfillmentsInput } from "@nexxt/contracts/logistics";
import { listUsersInput } from "@nexxt/contracts/party";
import { listCustomerOrdersInput } from "@nexxt/contracts/sales";
import { userQueries } from "@nexxt/party-ui";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from "@tanstack/react-router";
import * as z from "zod";
import { queryClient } from "./api";
import { bookingQueries } from "./features/booking/booking.queries";
import { claimQueries } from "./features/claims/claims.queries";
import { documentQueries } from "./features/documents/documents.queries";
import { confirmationQueries } from "./features/orders/confirmation.queries";
import { orderQueries } from "./features/orders/orders.queries";
import { organizationTabs, ownOrganizationQuery } from "./features/organization/organization.queries";
import { shipmentQueries } from "./features/shipments/shipments.queries";
import { FrontrowShell } from "./shell/frontrow-shell";

// The routes of Frontrow, in code (architettura-frontend.md), as Atelier's (ADR-216): the sign-in pages of
// @nexxt/account-ui under the root, without the session and without its API, so a refused account never loops;
// everything else inside the shell, which loads the user.

const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()();

const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: accountPaths.signIn,
  validateSearch: signInSearch,
  component: lazyRouteComponent(() => import("@nexxt/account-ui/pages"), "SignInPage"),
});

const secondStepRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: accountPaths.secondStep,
  validateSearch: secondStepSearch,
  component: lazyRouteComponent(() => import("@nexxt/account-ui/pages"), "SecondStepPage"),
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: accountPaths.forgotPassword,
  component: lazyRouteComponent(() => import("@nexxt/account-ui/pages"), "ForgotPasswordPage"),
});

// the paths of the links of identity's emails (ADR-147, ADR-163): the token is in the fragment
const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: accountPaths.resetPassword,
  component: lazyRouteComponent(() => import("@nexxt/account-ui/pages"), "ResetPasswordPage"),
});

const acceptInvitationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: accountPaths.acceptInvitation,
  component: lazyRouteComponent(() => import("@nexxt/account-ui/pages"), "AcceptInvitationPage"),
});

const confirmEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: accountPaths.confirmEmail,
  component: lazyRouteComponent(() => import("@nexxt/account-ui/pages"), "ConfirmEmailPage"),
});

// the personal link of a customer's e-TAF (ADR-175, ADR-218): a public page, without a session; the token is in the
// fragment. Atelier redirects here the links it was given before Frontrow existed.
const etafRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/etaf",
  component: lazyRouteComponent(() => import("./features/etaf/etaf-page"), "EtafPage"),
});

// the personal link to the booking of a campaign (ADR-237): a public page as the e-TAF's, the token in the fragment;
// a signed-in user reaches it from /appointments with a link of its own
const bookingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/booking",
  component: lazyRouteComponent(() => import("./features/booking/booking-page"), "BookingPage"),
});

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_shell",
  // a session that owes a second factor reaches only the page that adds one (ADR-151): the gateway refuses the rest
  beforeLoad: async ({ context, location }) => {
    const me = await context.queryClient.ensureQueryData(meQuery).catch((error: unknown) => {
      // no session: the sign-in, then back here (the client is already on its way, api.ts)
      if (error instanceof ApiRequestError && error.status === 401) throw redirect({ href: signInHref(location.href) });
      throw error;
    });
    if (me.mfaEnrollmentRequired && location.pathname !== accountPaths.security)
      throw redirect({ to: accountPaths.security });
  },
  component: FrontrowShell,
  errorComponent: RouteError,
});

const homeRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/",
  component: lazyRouteComponent(() => import("./features/home/home-page"), "HomePage"),
});

const accountRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: accountPaths.account,
  loader: ({ context }) => context.queryClient.ensureQueryData(preferencesQuery),
  component: lazyRouteComponent(() => import("@nexxt/account-ui/pages"), "AccountPage"),
});

const securityRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: accountPaths.security,
  // the sessions only for a session that owes no factor: the gateway refuses the rest until one is added
  loader: async ({ context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery);
    await Promise.all([
      queryClient.ensureQueryData(factorsQuery),
      me.mfaEnrollmentRequired ? null : queryClient.ensureQueryData(sessionsQuery),
    ]);
  },
  component: lazyRouteComponent(() => import("@nexxt/account-ui/pages"), "SecurityPage"),
});

// The orders of the customer (ADR-219): the ones in progress, or all of them, and a search by number, in the URL.
const ordersRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/orders",
  validateSearch: z.object({
    all: z.boolean().optional().catch(undefined),
    q: listCustomerOrdersInput.shape.q.catch(undefined),
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps: { all, q } }) =>
    context.queryClient.ensureInfiniteQueryData(orderQueries.list({ open: !all, q })),
  component: lazyRouteComponent(() => import("./features/orders/orders-page"), "OrdersPage"),
});

// An order by id or by number: its page keeps the frame, and says so when the order is not the customer's (404).
const orderRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/orders/$key",
  loader: ({ context, params }) => context.queryClient.ensureQueryData(orderQueries.detail(params.key)),
  component: lazyRouteComponent(() => import("./features/orders/order-page"), "OrderPage"),
  errorComponent: RouteError,
});

// The confirmation of an order (ADR-208, ADR-221): the path the emails link to, by the id of the order (`answerPath` of
// notifications), or by its number; its revisions are read by the id the order answers with.
const confirmationRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/orders/$key/confirmation",
  loader: async ({ context: { queryClient }, params }) => {
    const order = await queryClient.ensureQueryData(orderQueries.detail(params.key));
    await queryClient.ensureQueryData(confirmationQueries.ofOrder(order.id));
  },
  component: lazyRouteComponent(() => import("./features/orders/confirmation-page"), "ConfirmationPage"),
  errorComponent: RouteError,
});

// A claim on an order (B-55, ADR-228), from the order or from one of its lines (`line`): the form needs every line.
const newClaimRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/orders/$key/claim",
  validateSearch: z.object({ line: z.uuid().optional().catch(undefined) }),
  loader: async ({ context: { queryClient }, params }) => {
    const order = await queryClient.ensureQueryData(orderQueries.detail(params.key));
    await queryClient.ensureQueryData(orderQueries.allLines(order.id));
  },
  component: lazyRouteComponent(() => import("./features/claims/new-claim-page"), "NewClaimPage"),
  errorComponent: RouteError,
});

// The claims of the customer's orders (ADR-228), by order in the URL: the page of an order links here with its id.
const claimsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/claims",
  validateSearch: z.object({ salesOrder: listCustomerClaimsInput.shape.salesOrder.catch(undefined) }),
  loaderDeps: ({ search }) => search,
  loader: ({ context: { queryClient }, deps }) => queryClient.ensureInfiniteQueryData(claimQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/claims/claims-page"), "ClaimsPage"),
});

// One claim, from its link: a claim of another customer is the 404 of the route's error.
const claimRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/claims/$claimId",
  loader: ({ context: { queryClient }, params }) => queryClient.ensureQueryData(claimQueries.detail(params.claimId)),
  component: lazyRouteComponent(() => import("./features/claims/claim-page"), "ClaimPage"),
  errorComponent: RouteError,
});

// The customer's own organization (ADR-217): its addresses, users and contacts, each tab by its permission.
const organizationRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/organization",
  validateSearch: z.object({
    tab: z.enum(organizationTabs).optional().catch(undefined),
    // the filters of the users, and the user open in the panel
    q: listUsersInput.shape.q.catch(undefined),
    status: listUsersInput.shape.status.catch(undefined),
    role: listUsersInput.shape.role.catch(undefined),
    panel: z.uuid().optional().catch(undefined),
  }),
  loaderDeps: ({ search: { panel: _panel, ...deps } }) => deps,
  loader: async ({ context: { queryClient }, deps: { tab, ...filters } }) => {
    const [me] = await Promise.all([
      queryClient.ensureQueryData(meQuery),
      queryClient.ensureQueryData(ownOrganizationQuery),
    ]);
    if (tab === "users" && me.permissions.includes("user.read")) {
      await Promise.all([
        queryClient.ensureInfiniteQueryData(userQueries.list(filters)),
        queryClient.ensureQueryData(userQueries.options()),
      ]);
    }
  },
  component: lazyRouteComponent(() => import("./features/organization/organization-page"), "OrganizationPage"),
});

// The documents of the customer's orders with their PDF (ADR-220): by type, number and order, in the URL.
const documentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/documents",
  validateSearch: z.object({
    type: listCustomerDocumentsInput.shape.type.catch(undefined),
    q: listCustomerDocumentsInput.shape.q.catch(undefined),
    salesOrder: listCustomerDocumentsInput.shape.salesOrder.catch(undefined),
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context: { queryClient }, deps }) => queryClient.ensureInfiniteQueryData(documentQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/documents/documents-page"), "DocumentsPage"),
});

// The shipments of the customer's orders with their parcels and tracking (ADR-220): by number and order, in the URL.
const shipmentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/shipments",
  validateSearch: z.object({
    q: listCustomerFulfillmentsInput.shape.q.catch(undefined),
    salesOrder: listCustomerFulfillmentsInput.shape.salesOrder.catch(undefined),
  }),
  loaderDeps: ({ search }) => search,
  loader: ({ context: { queryClient }, deps }) => queryClient.ensureInfiniteQueryData(shipmentQueries.list(deps)),
  component: lazyRouteComponent(() => import("./features/shipments/shipments-page"), "ShipmentsPage"),
});

// One shipment, from its link: a shipment of another customer is the 404 of the route's error.
const shipmentRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/shipments/$fulfillmentId",
  loader: ({ context: { queryClient }, params }) =>
    queryClient.ensureQueryData(shipmentQueries.detail(params.fulfillmentId)),
  component: lazyRouteComponent(() => import("./features/shipments/shipments-page"), "ShipmentPage"),
  errorComponent: RouteError,
});

// The campaigns the signed-in customer can book (ADR-237), each opening the page of the booking.
const appointmentsRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: "/appointments",
  loader: ({ context }) => context.queryClient.ensureQueryData(bookingQueries.campaigns()),
  component: lazyRouteComponent(() => import("./features/booking/appointments-page"), "AppointmentsPage"),
});

const routeTree = rootRoute.addChildren([
  signInRoute,
  secondStepRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  acceptInvitationRoute,
  confirmEmailRoute,
  etafRoute,
  bookingRoute,
  shellRoute.addChildren([
    homeRoute,
    ordersRoute,
    orderRoute,
    documentsRoute,
    shipmentsRoute,
    shipmentRoute,
    confirmationRoute,
    newClaimRoute,
    claimsRoute,
    claimRoute,
    appointmentsRoute,
    organizationRoute,
    accountRoute,
    securityRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  defaultPendingMs: 300,
  defaultViewTransition: true,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
